import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { getFilterValueForStorage } from '../../../utils/filterUtils';
import MultiRangeSlider from '@src/stories/elements/MultirangeSlider/MultiRangeSlider';

// Debounce utility function
const useDebounce = (callback: (...args: any[]) => void, delay: number) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  return useCallback(
    (...args: any[]) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay]
  );
};

interface CollectionSidebarPriceProps {
  filter: any;
  activeFilters?: Record<string, any>;
  pendingFilters?: Record<string, any>;
  setPendingFilters: (filters: Record<string, any>) => void;
  applyFilters: (filters: Record<string, any>) => void;
  applyInstantly?: boolean;
  isSearchSidebar?: boolean;
  priceRange?: [number, number];
  setPriceRange?: (range: [number, number]) => void;
  originalMinPrice?: number;
  originalMaxPrice?: number;
}

export const CollectionSidebarPrice = ({
  filter,
  activeFilters = {},
  pendingFilters = {},
  setPendingFilters,
  applyFilters,
  applyInstantly,
  isSearchSidebar,
  priceRange,
  setPriceRange,
  originalMinPrice,
  originalMaxPrice
}: CollectionSidebarPriceProps) => {
  // Extract min and max prices from filter values - memoized to prevent infinite re-renders
  const priceValues = useMemo(
    () =>
      filter.values
        .map((value: any) => {
          // First try to use the input field (already parsed object)
          if (value.input) {
            // Handle string input that needs parsing
            if (typeof value.input === 'string') {
              try {
                const parsedInput = JSON.parse(value.input);
                if (parsedInput.price) {
                  return { min: parsedInput.price.min, max: parsedInput.price.max };
                }
              } catch (e) {
                // Silent fallback
              }
            }
            // Handle already parsed object
            else if (typeof value.input === 'object' && value.input.price) {
              return { min: value.input.price.min, max: value.input.price.max };
            }
          }

          // Fallback to parsing the key field
          try {
            const parsed = JSON.parse(value.input);
            if (parsed.price) {
              return { min: parsed.price.min, max: parsed.price.max };
            }
          } catch (e) {
            // Handle non-JSON price values
            const parts = value.input.split('-');
            if (parts.length === 2) {
              const minPrice = parseFloat(parts[0]);
              const maxPrice = parseFloat(parts[1]);
              if (!isNaN(minPrice) && !isNaN(maxPrice)) {
                return { min: minPrice, max: maxPrice };
              }
            }

            // Try to extract from label or other formats
            if (value.label && value.label !== 'Price') {
              const labelParts = value.label.split('-');
              if (labelParts.length === 2) {
                const minPrice = parseFloat(labelParts[0].replace(/[^0-9.]/g, ''));
                const maxPrice = parseFloat(labelParts[1].replace(/[^0-9.]/g, ''));
                if (!isNaN(minPrice) && !isNaN(maxPrice)) {
                  return { min: minPrice, max: maxPrice };
                }
              }
            }
          }

          return null;
        })
        .filter(Boolean),
    [filter.values]
  );

  if (priceValues.length === 0) {
    return null;
  }

  // Store the original absolute price bounds in a ref to prevent them from changing
  const originalPriceBounds = useRef(null);

  // Use passed original bounds if available, otherwise calculate from current data
  if (!originalPriceBounds.current) {
    if (originalMinPrice !== undefined && originalMaxPrice !== undefined) {
      // Use the passed original bounds (preferred method)
      originalPriceBounds.current = {
        min: originalMinPrice,
        max: originalMaxPrice
      };
    } else if (priceValues.length > 0) {
      // Fallback: calculate from current price values (may be filtered)
      const allMinPrices = priceValues.map((p: any) => p.min);
      const allMaxPrices = priceValues.map((p: any) => p.max);

      originalPriceBounds.current = {
        min: Math.min(...allMinPrices),
        max: Math.max(...allMaxPrices)
      };
    }
  }

  // Use original bounds for the slider's min/max range
  const absoluteMinPrice = originalPriceBounds.current?.min ?? 0;
  const absoluteMaxPrice = originalPriceBounds.current?.max ?? 1000;

  const currentPriceFilter = activeFilters[filter.key] || [];

  const [initialMin, initialMax] = useMemo(() => {
    let currentMin = absoluteMinPrice;
    let currentMax = absoluteMaxPrice;
    if (currentPriceFilter.length > 0) {
      const filterValue = currentPriceFilter[0];
      if (filterValue.includes('-')) {
        const [min, max] = filterValue.split('-').map(Number);
        if (!isNaN(min) && !isNaN(max)) {
          currentMin = min;
          currentMax = max;
        }
      } else {
        try {
          const parsed = JSON.parse(filterValue);
          if (parsed.price) {
            currentMin = parsed.price.min;
            currentMax = parsed.price.max;
          }
        } catch {}
      }
    }
    return [currentMin, currentMax];
  }, [currentPriceFilter, absoluteMinPrice, absoluteMaxPrice]);

  // Initialize price range from external state or use defaults
  // If the price filter is removed, always use original bounds for currentPriceRange
  const isFilterRemoved =
    (!activeFilters[filter.key] || activeFilters[filter.key].length === 0) &&
    (!pendingFilters[filter.key] || pendingFilters[filter.key].length === 0);
  const currentPriceRange = isFilterRemoved
    ? [originalPriceBounds.current.min, originalPriceBounds.current.max]
    : priceRange || [initialMin, initialMax];

  // Local state for input values to prevent focus loss during typing
  const [localMinValue, setLocalMinValue] = useState<string | number>(currentPriceRange[0]);
  const [localMaxValue, setLocalMaxValue] = useState<string | number>(currentPriceRange[1]);

  // Track if we're currently typing to prevent external updates from overriding local input
  const isTypingRef = useRef(false);

  // Update local values when currentPriceRange changes (from external sources like slider)
  // but only if we're not currently typing in the inputs
  useEffect(() => {
    if (!isTypingRef.current) {
      // If the filter is removed and currentPriceRange matches original bounds, force reset
      if (
        currentPriceRange[0] === originalPriceBounds.current?.min &&
        currentPriceRange[1] === originalPriceBounds.current?.max
      ) {
        setLocalMinValue(originalPriceBounds.current.min);
        setLocalMaxValue(originalPriceBounds.current.max);
      } else {
        setLocalMinValue(currentPriceRange[0]);
        setLocalMaxValue(currentPriceRange[1]);
      }
    }
  }, [currentPriceRange]);

  // Initialize external state only once when component mounts or when filter changes from URL
  useEffect(() => {
    if (!priceRange && setPriceRange) {
      setPriceRange([initialMin, initialMax]);
    }
  }, [initialMin, initialMax, priceRange, setPriceRange]);

  const applyPriceFilter = useCallback(
    (range: [number, number]) => {
      // If range is not set or is the full range, remove the price filter
      const isUnset =
        range == null ||
        range.length !== 2 ||
        range[0] === undefined ||
        range[1] === undefined ||
        (Number(range[0]) === Number(absoluteMinPrice) && Number(range[1]) === Number(absoluteMaxPrice));

      const newFiltersData = { ...activeFilters };
      delete newFiltersData[filter.key];
      if (!isUnset) {
        newFiltersData[filter.key] = [`${Number(range[0])}-${Number(range[1])}`];
      }

      const baseFilters = { ...(applyInstantly ? activeFilters : pendingFilters) };
      if (!isUnset) {
        const valueForStorage = getFilterValueForStorage(filter.key, newFiltersData[filter.key], []);
        baseFilters[filter.key] = [valueForStorage];
      } else {
        // Remove pending price filter if unset
        if (baseFilters[filter.key]) {
          delete baseFilters[filter.key];
        }
      }
      setPendingFilters(baseFilters);
      if (applyInstantly) applyFilters(newFiltersData);
    },
    [
      activeFilters,
      filter.key,
      absoluteMinPrice,
      absoluteMaxPrice,
      applyInstantly,
      pendingFilters,
      setPendingFilters,
      applyFilters
    ]
  );

  const handleInputChange = useCallback(
    (index: number, value: string | number) => {
      let numValue = Number(value);

      // Apply validation and constraints
      if (isNaN(numValue)) {
        numValue = index === 0 ? absoluteMinPrice : absoluteMaxPrice;
      } else {
        // Enforce min/max constraints
        if (index === 0) {
          // Minimum field constraints
          if (numValue < absoluteMinPrice) numValue = absoluteMinPrice;
          if (numValue > currentPriceRange[1]) numValue = currentPriceRange[1];
        } else {
          // Maximum field constraints
          if (numValue > absoluteMaxPrice) numValue = absoluteMaxPrice;
          if (numValue < currentPriceRange[0]) numValue = currentPriceRange[0];
        }
      }

      const newRange: [number, number] = [...currentPriceRange] as [number, number];
      if (index === 0) newRange[0] = numValue;
      else newRange[1] = numValue;

      // Update local state to match the validated value
      if (index === 0) {
        setLocalMinValue(numValue);
      } else {
        setLocalMaxValue(numValue);
      }

      // If both min and max are at their absolute bounds, treat as unset
      if (newRange[0] === absoluteMinPrice && newRange[1] === absoluteMaxPrice) {
        // Reset to original bounds, not last filtered values
        if (setPriceRange)
          setPriceRange([originalPriceBounds.current.min, originalPriceBounds.current.max] as [number, number]);
        setLocalMinValue(originalPriceBounds.current.min);
        setLocalMaxValue(originalPriceBounds.current.max);
        applyPriceFilter([originalPriceBounds.current.min, originalPriceBounds.current.max] as [number, number]);
      } else {
        if (setPriceRange) setPriceRange(newRange);
        applyPriceFilter(newRange);
      }

      // Clear typing flag after processing
      isTypingRef.current = false;
    },
    [currentPriceRange, absoluteMinPrice, absoluteMaxPrice, setPriceRange, applyPriceFilter]
  );

  // Debounced version of handleInputChange to prevent immediate re-renders
  const debouncedHandleInputChange = useDebounce(handleInputChange, 1000);

  // Handle input focus to set typing flag
  const handleInputFocus = useCallback(() => {
    isTypingRef.current = true;
  }, []);

  // Handle input blur to clear typing flag
  const handleInputBlur = useCallback(() => {
    // Small delay to ensure any pending updates complete
    setTimeout(() => {
      isTypingRef.current = false;
    }, 100);
  }, []);

  // Handle local input changes without triggering filter updates immediately
  const handleLocalInputChange = useCallback(
    (index: number, value: string) => {
      // Set typing flag to prevent external updates
      isTypingRef.current = true;

      // Always update local state immediately for responsive UI (no validation here to prevent unfocusing)
      if (index === 0) {
        setLocalMinValue(value === '' ? '' : Number(value));
      } else {
        setLocalMaxValue(value === '' ? '' : Number(value));
      }

      // Debounce the actual filter update with validation
      debouncedHandleInputChange(index, value);
    },
    [debouncedHandleInputChange]
  );

  const handleSliderChange = useCallback(
    ({ min, max }: { min: number; max: number }) => {
      const newRange: [number, number] = [min, max];
      // Only update if the range actually changed to prevent infinite loops
      if (currentPriceRange[0] !== min || currentPriceRange[1] !== max) {
        // If slider is reset to full range, use original bounds
        if (min === absoluteMinPrice && max === absoluteMaxPrice) {
          if (setPriceRange)
            setPriceRange([originalPriceBounds.current.min, originalPriceBounds.current.max] as [number, number]);
          setLocalMinValue(originalPriceBounds.current.min);
          setLocalMaxValue(originalPriceBounds.current.max);
          applyPriceFilter([originalPriceBounds.current.min, originalPriceBounds.current.max] as [number, number]);
        } else {
          if (setPriceRange) setPriceRange(newRange);
          applyPriceFilter(newRange);
        }
      }
    },
    [currentPriceRange, setPriceRange, applyPriceFilter]
  );

  return (
    <div className='h-full w-full'>
      <div className='flex justify-between items-center mt-4 mb-2'>
        <MultiRangeSlider
          min={absoluteMinPrice}
          max={absoluteMaxPrice}
          initialMin={currentPriceRange[0]}
          initialMax={currentPriceRange[1]}
          onChange={({ min, max }) => handleSliderChange({ min, max })}
        />
      </div>
      <div className='flex gap-2'>
        <div className='flex-1'>
          <div className='border border-current rounded-lg p-2 flex flex-col items-start'>
            <label
              className='block b7--medium mb-1'
              htmlFor={`min-price-${filter.key}`}
            >
              Minimum
            </label>
            <input
              id={`min-price-${filter.key}`}
              type='number'
              value={localMinValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleLocalInputChange(0, e.target.value)}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              placeholder='Input Number'
              className='w-full rounded focus:outline-none border-none'
              min={absoluteMinPrice}
              max={localMaxValue}
            />
          </div>
        </div>
        <div className='flex-1'>
          <div className='border border-current rounded-lg p-2 flex flex-col items-start'>
            <label
              className='block b7--medium mb-1'
              htmlFor={`max-price-${filter.key}`}
            >
              Maximum
            </label>
            <input
              id={`max-price-${filter.key}`}
              type='number'
              value={localMaxValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleLocalInputChange(1, e.target.value)}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              placeholder='Input Number'
              className='w-full rounded focus:outline-none border-none'
              min={localMinValue}
              max={absoluteMaxPrice}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
